import React, { FC, LegacyRef, useEffect, useRef, useState } from 'react';
import {
  Icon, spacingCalculator,
} from '@digando/react-component-library';
import styled from '@emotion/styled';
import { ProductTypeImage, ProductTypeImageFragment } from '../../@types/codegen/graphql';
import { RemoteImage } from '../../lib/remote-image';
import { fallbackImageUrl } from '../../helper/fallback-image';
import { ProductImageView } from './product-image-view';

export interface IProductImageGalleryProps {
  images: ProductTypeImage[];
}

const GalleryImage: FC<{ image: ProductTypeImageFragment; priority: boolean; }> = ({ image, priority }) => {
  return (
    <RemoteImage
      sources={[
        { maxWidth: '360px', src: fallbackImageUrl({ url: image.url, w: '342', q: '60', crop: false }) },
        { maxWidth: '768px', src: fallbackImageUrl({ url: image.url, w: '730', q: '60', crop: false }) },
        { maxWidth: '1024px', src: fallbackImageUrl({ url: image.url, w: '910', q: '60', crop: false }) },
        { maxWidth: '1200px', src: fallbackImageUrl({ url: image.url, w: '436', q: '60', crop: false }) },
        { maxWidth: '1400px', src: fallbackImageUrl({ url: image.url, w: '615', q: '60', crop: false }) },
        { maxWidth: '2000px', src: fallbackImageUrl({ url: image.url, w: '704', q: '60', crop: false }) },
      ]}
      src={fallbackImageUrl({ url: image.url, w: '342', q: '60', crop: false })}
      alt={image?.alt ?? ''}
      title={image.title}
      blurSrc={fallbackImageUrl({ url: image.url, w: '704', blur: '150', q: '60' })}
      priority={priority}
      width={image.metadata.width}
      height={image.metadata.height}
      className={'is-single-image'}
    />
  );
};

const Gallery: FC<IProductImageGalleryProps> = ({ images }) => {
  const [sliderImage, setSliderImage] = useState<number>(0);
  const [start, setStart] = useState<number | null>(null);
  const [isProductViewVisible, setIsProductViewVisible] = useState(false);
  const ref = useRef<HTMLDivElement>();

  useEffect(() => {
    const onTouchStart = (event: TouchEvent) => {
      setStart(event.touches?.[0].pageX ?? 0);
    };
    const onTouchEnd = (event: TouchEvent) => {
      if (null !== start) {
        const x = event.changedTouches?.[0].pageX ?? 0;
        const diff = x - start;
        setStart(null);

        if (-100 > diff) {
          nextImage();
        }

        if (100 < diff) {
          prevImage();
        }
      }
    };

    if (ref?.current) {
      ref.current.addEventListener('touchstart', onTouchStart);
      ref.current.addEventListener('touchend', onTouchEnd);
    }

    return () => {
      if (ref?.current) {
        ref.current.removeEventListener('touchstart', onTouchStart);
        ref.current.removeEventListener('touchend', onTouchEnd);
      }
    };
  }, [ref, start]);

  useEffect(() => {
    setSliderImage(0);
  }, [JSON.stringify(images)]);

  const prevImage = () => {
    let prevIndex = sliderImage - 1;

    if (-1 === prevIndex) {
      prevIndex = images.length - 1;
    }

    setSliderImage(prevIndex);
  };

  const nextImage = () => {
    let nextImage = sliderImage + 1;

    if (nextImage > (images.length - 1)) {
      nextImage = 0;
    }

    setSliderImage(nextImage);
  };

  const showProductView = () => {
    setIsProductViewVisible(true);
  }

  const hideProductView = () => {
    setIsProductViewVisible(false);
  }

  return (
    <>
      <GalleryContainer index={sliderImage} ref={ref as LegacyRef<HTMLDivElement>}>
        {1 < images.length && (<>
          <NextArrow onClick={nextImage}><Icon size={'28px'} icon={'right'} color={`var(--secondary-color)`} /></NextArrow>
          <PrevArrow onClick={prevImage}><Icon size={'28px'} icon={'right'} color={`var(--secondary-color)`} /></PrevArrow>
        </>)}

        <ul>
          {images.map((image, index) => {
            return (
              <li key={`image-${index}`} onClick={() => showProductView()}>
                <GalleryImage image={image} priority={0 === index} />
              </li>
            );
          })}
        </ul>
      </GalleryContainer>

      {1 < images.length && (
        <Pagination>
          {images.map((image, index) => {
            return <PaginationItem
              key={`pagination-${index}`}
              isActive={sliderImage === index}
              onClick={() => setSliderImage(index)}
            />;
          })}
        </Pagination>
      )}

      <ProductImageView
        isVisible={isProductViewVisible}
        onClose={hideProductView}
        currentImage={images[sliderImage]}
      />
    </>
  );
};

export const ProductImageGallery = React.memo(Gallery, (oldProps, newProps) => {
  const oldImageIds = oldProps.images.map(image => image.url);
  const newImageIds = newProps.images.map(image => image.url);

  return oldImageIds.join(';') === newImageIds.join(';');
});

const GalleryContainer = styled.div<{ index: number }>`
  display: block;
  position: relative;
  width: 100%;
  height: auto;
  overflow: hidden;
  
  border-radius: var(--container-border-radius);

  ul {
    width: 100%;
    height: auto;
    white-space: nowrap;
    transform: translateX(calc(${p => p.index} * -100%));
    transition: all 1s ease;
    margin: 0;
    padding: 0;
    list-style: none;
  }

  li {
    display: inline-block;
    width: 100%;
    margin: 0;
    padding: 0;
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    object-position: center center;
    cursor: pointer;
  }
`;

const Arrow = styled.div`
  position: absolute;
  top: 50% !important;
  right: -50px !important;
  left: auto !important;
  border-radius: 50%;
  width: 28px !important;
  height: 28px !important;
  z-index: 80;
  cursor: pointer;

  span {
    color: var(--secondary-color);
  }
`;

const NextArrow = styled(Arrow)`
  right: 0px !important;
`;

const PrevArrow = styled(Arrow)`
  transform: rotate(180deg);
  right: auto !important;
  left: 0px !important;
`;

const Pagination = styled.div`
  text-align: center;
`;

const PaginationItem = styled.div<{ isActive: boolean; }>`
  display: inline-block;
  height: 3px;
  width: ${p => p.isActive ? '30px' : '15px'};
  background-color: ${p => p.isActive ? `var(--secondary-color)` : `var(--font-color-disabled)`};
  margin-left: ${spacingCalculator(0.5)};
  margin-right: ${spacingCalculator(0.5)};
  transition: all 0.6s ease;
  cursor: pointer;
`;
